import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch, apiFetchBlob, apiUpload } from "@/lib/api/client"
import { readTextPreview } from "@/lib/stored-file"
import type { ApiResource } from "@/lib/api/types"
import type {
  DriveFile,
  DriveFolder,
  DriveListing,
  DriveOwner,
  DriveUploadInput,
  ApiDriveFile,
  ApiDriveFolder,
  ApiDriveListing,
} from "@/types/drive"

function ownerPath(owner: DriveOwner): string {
  const resource = owner.type === "firm" ? "firms" : "individuals"

  return `/${resource}/${owner.id}`
}

export function toDriveFolder(data: ApiDriveFolder): DriveFolder {
  return {
    id: data.id,
    parentId: data.parent_id,
    name: data.name,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

export function toDriveFile(data: ApiDriveFile): DriveFile {
  return {
    id: data.id,
    folderId: data.folder_id,
    name: data.name,
    contentType: data.content_type,
    kind: data.kind,
    byteSize: data.byte_size,
    sha256: data.sha256,
    uploadedBy: data.uploaded_by?.name ?? null,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

function toDriveListing(data: ApiDriveListing): DriveListing {
  return {
    folder: data.folder ? toDriveFolder(data.folder) : null,
    breadcrumb: data.breadcrumb.map(toDriveFolder),
    folders: data.folders.map(toDriveFolder),
    files: data.files.map(toDriveFile),
    summary: data.summary,
  }
}

async function fileContent(fileId: string, signal?: AbortSignal) {
  return await apiFetchBlob(`/files/${fileId}/view`, { signal })
}

export const driveQueries = {
  text: (fileId: string) =>
    queryOptions({
      queryKey: apiKey(`/files/${fileId}/view`, { as: "text" }),
      queryFn: async ({ signal }) =>
        await readTextPreview(await fileContent(fileId, signal)),
      gcTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
      retry: false,
    }),
  detail: (fileId: string) =>
    queryOptions({
      queryKey: apiKey(`/files/${fileId}`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiDriveFile>>(
          `/files/${fileId}`,
          { signal }
        )

        return toDriveFile(data)
      },
    }),
  view: (fileId: string) =>
    queryOptions({
      queryKey: apiKey(`/files/${fileId}/view`),
      queryFn: async ({ signal }) => await fileContent(fileId, signal),
      gcTime: 0,
      staleTime: 0,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
    }),
  list: (owner: DriveOwner, folderId: string | null) =>
    queryOptions({
      queryKey: apiKey(`${ownerPath(owner)}/files`, { folder_id: folderId }),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiDriveListing>>(
          `${ownerPath(owner)}/files`,
          { params: { folder_id: folderId }, signal }
        )

        return toDriveListing(data)
      },
    }),
}

export function useCreateDriveFolder(owner: DriveOwner) {
  return useMutation({
    mutationFn: async (input: { name: string; parentId: string | null }) => {
      const { data } = await apiFetch<ApiResource<ApiDriveFolder>>(
        `${ownerPath(owner)}/folders`,
        {
          method: "POST",
          body: { name: input.name, parent_id: input.parentId },
        }
      )

      return toDriveFolder(data)
    },
  })
}

export function useUploadDriveFile(owner: DriveOwner) {
  return useMutation({
    mutationFn: async ({
      file,
      folderId,
      onProgress,
      path,
      signal,
    }: DriveUploadInput) => {
      const body = new FormData()
      body.append("file", file)

      if (folderId) {
        body.append("folder_id", folderId)
      }

      if (path) {
        body.append("path", path)
      }

      const { data } = await apiUpload<ApiResource<ApiDriveFile>>(
        `${ownerPath(owner)}/files`,
        { body, onProgress, signal }
      )

      return toDriveFile(data)
    },
  })
}

export function useRenameDriveFile() {
  return useMutation({
    mutationFn: async (input: { fileId: string; name: string }) => {
      const { data } = await apiFetch<ApiResource<ApiDriveFile>>(
        `/files/${input.fileId}`,
        { method: "PATCH", body: { name: input.name } }
      )

      return toDriveFile(data)
    },
  })
}

export function useRenameDriveFolder() {
  return useMutation({
    mutationFn: async (input: { folderId: string; name: string }) => {
      const { data } = await apiFetch<ApiResource<ApiDriveFolder>>(
        `/folders/${input.folderId}`,
        { method: "PATCH", body: { name: input.name } }
      )

      return toDriveFolder(data)
    },
  })
}

export function useDeleteDriveFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (fileId: string) =>
      await apiFetch<void>(`/files/${fileId}`, { method: "DELETE" }),
    onSuccess: (_data, fileId) => {
      queryClient.removeQueries({ queryKey: apiKey(`/files/${fileId}`) })
    },
  })
}

export function useDeleteDriveFolder() {
  return useMutation({
    mutationFn: async (folderId: string) =>
      await apiFetch<void>(`/folders/${folderId}`, { method: "DELETE" }),
  })
}

export function useDownloadDriveFile() {
  return useMutation({
    mutationFn: async (fileId: string) =>
      await apiFetchBlob(`/files/${fileId}/download`),
  })
}
